import { API_STATUS, JAVA_API_STATUS } from '../../../../../../../utils/axios-interceptor';
import {
    deleteMnemosyneFiles,
    downloadFilePresigned,
    downloadFilePresignedBulk,
    downloadFileUsingURL,
    fetchSignedUrlToUpload,
    getDocMetaData,
    saveKycDocs,
    saveMetaDataInDocService,
    uploadFileApi,
} from './services';
import { toast } from 'react-toastify';

export const getSignedUrl = async (fileData: any, uploadConfig) => {
    const data = {
        fileName: fileData?.file?.name,
        fileSizeKb: fileData?.file?.size / 1_000,
        fileType: fileData.file.type,
        categoryId: uploadConfig.categoryId,
        mnemosyneOrgId: uploadConfig.mnemosyneOrgId,
        ...(fileData?.file?.checkForDuplication && { checkForDuplication: fileData?.file?.checkForDuplication }),
        ...(!!uploadConfig?.accountNumber && { dk1: uploadConfig?.accountNumber }),
    };
    try {
        const res = await fetchSignedUrlToUpload(data, uploadConfig.assesseeOrgId);
        if (res?.data?.responseData?.responseCode === 'OK') {
            return res.data;
        }
        throw new Error(res?.data?.responseData?.responseMessage ?? 'Some error occurred');
    } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        throw new Error(`Error in fetching signed url: ${errorMessage}`);
    }
};

export const uploadFile = async (fileData: any, signedURL) => {
    try {
        const uploadResponse = await uploadFileApi(signedURL, fileData);
        return uploadResponse;
    } catch (err) {
        console.error(err);
        return {
            url: '',
        };
    }
};

export const saveDocMetaDataInDocService = async ({ input, orgId }: { input: object; orgId?: string }): Promise<any> => {
    try {
        const res = await saveMetaDataInDocService(input, orgId);
        const responseCode = res?.data?.responseData?.responseCode;
        if (responseCode === JAVA_API_STATUS.SUCCESS || responseCode === API_STATUS.MNEMOSYNE_SUCCESS) return res?.data?.fileMetadata;
        // On failure return the full response so callers can surface responseData.responseMessage in the toast.
        console.error('Error occurred in saveDocMetaDataInDocService : ' + res?.data?.responseData?.responseMessage);
        return res?.data;
    } catch (err) {
        console.error('Error occurred in saveDocMetaDataInDocService : ' + err);
        // captureMessagesViaErrorLogger(err);
    }
};

export const downloadFile = async (docId, onSuccess = () => {}, fileName?: string, onError = () => {}, fileType = null) => {
    const response = await getPresignedUrl({ docId: Number(docId) }, true);

    if (response?.uri) {
        const url = response.uri;
        // Derive a filename from the URL path when possible; never let a
        // malformed/empty presigned URI throw and skip the onError branch below.
        let fileNameFromUrl: string | null = null;
        try {
            fileNameFromUrl = new URL(url)?.pathname?.split('/')?.pop() || null;
        } catch {
            fileNameFromUrl = null;
        }
        // use the above url and create a blob and download the file
        const res = await fetch(url);
        if (res.status === 200) {
            const blob = await res.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName || fileNameFromUrl || 'file';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('File Downloaded Successfully');
        } else {
            toast.error('Error in downloading file');
        }
        if (typeof onSuccess === 'function') onSuccess?.();
    } else {
        onError?.();
        console.error('Error occurred while downloading file with docId: ' + docId);
    }
};

export const getPresignedUrl = async ({ docId }: { docId: number }, markAsDownloaded: boolean): Promise<any> => {
    const getFileType = (mimeType: string): string => {
        if (mimeType === 'application/.pdf') return 'application/pdf';
        if (mimeType === 'application/text') return 'text/plain';
        return mimeType;
    };
    try {
        const res = await downloadFilePresigned(docId, markAsDownloaded);
        if (res?.data?.responseData?.responseCode === API_STATUS.MNEMOSYNE_SUCCESS)
            return {
                uri: res?.data?.signedUrl,
                fileType: getFileType(res?.data?.mimeType),
            };
        throw new Error(res?.data?.responseData?.responseMessage ?? 'Some error occurred');
    } catch (err) {
        console.error(err);
    }
};

export const downloadFileUsingURLHelper = async (url: string, bucketName: string) => {
    try {
        const res: any = await downloadFileUsingURL(url, bucketName);
        if (res.status === 200) {
            return res.data.signedUrl;
        } else {
            toast.error('Error in downloading file');
        }
    } catch (err) {
        console.error(err);
    }
};
export const saveKycDocsHelper = async (data: any, handler: any) => {
    try {
        const res: any = await saveKycDocs(data);
        if (res.data.responseData.responseCode === JAVA_API_STATUS.SUCCESS) {
            handler?.onSuccess();
        } else {
            toast.error(res.data.responseData.responseMessage);
        }
    } catch (err) {
        console.error(err);
    }
};

export const getDocMetaDataHelper = async (docId: string) => {
    try {
        const res: any = await getDocMetaData(docId);
        if (res.status === 200) {
            return res.data;
        } else {
            toast.error('Failed to fetch doc meta data, Please Try Again');
        }
    } catch (err) {
        console.error(err);
    }
};

/**
 * Fetch presigned download URLs for multiple docIds in a single call.
 * Returns a map of docId → presigned URL.
 */
export const getPresignedUrlBulk = async (docIds: number[]): Promise<Record<number, string>> => {
    try {
        const res = await downloadFilePresignedBulk(docIds);
        if (res?.data?.responseData?.responseCode === API_STATUS.MNEMOSYNE_SUCCESS) {
            const bulkMap: Record<number, string> = {};
            const presignedUrlsBulk = res?.data?.presignedUrlsBulk || {};
            for (const [fileId, output] of Object.entries(presignedUrlsBulk)) {
                const signedUrl = (output as any)?.signedUrl;
                if (signedUrl) bulkMap[Number(fileId)] = signedUrl;
            }
            return bulkMap;
        }
        throw new Error(res?.data?.responseData?.responseMessage ?? 'Bulk presigned URL fetch failed');
    } catch (err) {
        console.error('Error in getPresignedUrlBulk:', err);
        return {};
    }
};

/**
 * Delete files from Mnemosyne by docIds (soft delete).
 */
export const deleteEditorImages = async (docIds: number[]): Promise<void> => {
    if (!docIds.length) return;
    try {
        await deleteMnemosyneFiles(docIds);
    } catch (err) {
        console.error('Error deleting editor images from Mnemosyne:', err);
    }
};
